import * as React from "react";
import Highlighter from "react-highlight-words";
import { KeyTerm } from "./KeyTerm";
import { terms } from "@apollo/pedia";

export const HighlightApollopediaTerms: React.FC<{
  children: React.ReactNode;
  maxHighlightExtension?: number;
}> = ({ children, maxHighlightExtension = 3 }) => {
  return (
    <>
      {React.Children.map(children, (child) => {
        if (typeof child !== "string") {
          return child;
        }
        return (
          <Highlighter
            searchWords={Array.from(terms.keys())}
            textToHighlight={child}
            findChunks={({ searchWords, textToHighlight }) => {
              const chunks = [];
              const normalizedTextToHighlight = textToHighlight.toLowerCase();
              for (const word of searchWords) {
                if (typeof word !== "string" || !word.length) {
                  continue;
                }
                const index = normalizedTextToHighlight.indexOf(
                  word.toLowerCase()
                );
                if (index === -1) {
                  continue;
                }
                const term = terms.get(word.toLowerCase());
                if (
                  term &&
                  "surroundingContextsToAvoid" in term.frontmatter &&
                  Array.isArray(term.frontmatter.surroundingContextsToAvoid)
                ) {
                  // Escape characters for use in regexp
                  const escapedContexts =
                    term.frontmatter.surroundingContextsToAvoid.map(
                      (c: string) => c.replace(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&")
                    );
                  if (
                    new RegExp(escapedContexts.join("|"), "i").test(
                      normalizedTextToHighlight
                    )
                  ) {
                    continue;
                  }
                }
                let endIndex = index + word.length;
                for (let i = 0; i < maxHighlightExtension; i++) {
                  const isAlpha = /[a-z]/.test(
                    normalizedTextToHighlight[endIndex]
                  );
                  if (isAlpha) {
                    endIndex++;
                  } else {
                    break;
                  }
                }
                // If we have iterated and the term still has a letter next, reset the endIndex
                // else the hightlight would cover _queryabl_e where only the e is unhighlighted
                if (/[a-z]/.test(normalizedTextToHighlight[endIndex])) {
                  endIndex = index + word.length;
                }
                chunks.push({
                  start: index,
                  end: endIndex,
                });
              }
              return chunks;
            }}
            highlightTag={({ children }) => {
              const searchTerm = children.toLowerCase();
              let term = terms.get(searchTerm);
              let actualTerm = searchTerm;
              let backtrack = 0;
              while (!term && backtrack <= maxHighlightExtension) {
                // Try to find a term while slicing the backtrack to support plurals
                // and other word variations
                // e.g. documents -> document !Match
                // but with the maximum extension in mind
                // e.g. documentation -> documentatio -> documentati !No Match, max extension reached
                actualTerm = searchTerm.slice(0, -backtrack);
                term = terms.get(actualTerm);
                backtrack++;
              }
              if (!term) {
                console.warn(`No definition found for ${children}`);
                return children;
              }
              return <KeyTerm term={children} definition={term.definition} />;
            }}
          />
        );
      })}
    </>
  );
};
